import { useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { QRCode } from 'react-qrcode-logo';
import { Container, Grid, Box, Typography, Card, Button } from '@mui/material';
import { DownloadForOffline, PictureAsPdf } from '@mui/icons-material';
import useWindowDimensions from '@hooks/useWindowDimensions';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';

const QRPreregistro = forwardRef((props, ref) => {
  const { dataQR, mensaje, dataEvent, view = false } = props
  const { width } = useWindowDimensions();

  const qrRef = useRef();
  const [imageSrc, setImageSrc] = useState(null);

  const backgroundImageUrl = dataEvent?.imgPlantillaQR || false;

  const generateImage = (download, callback) => {
    const qrCanvas = qrRef.current.querySelector('canvas');
    if (!qrCanvas) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (backgroundImageUrl) {
      // CON BACKGROUND
      const backgroundImage = new Image();
      backgroundImage.crossOrigin = 'Anonymous';
      backgroundImage.src = backgroundImageUrl;

      backgroundImage.onload = () => {
        canvas.width = backgroundImage.width;
        canvas.height = backgroundImage.height;

        ctx.drawImage(backgroundImage, 0, 0);

        const qrImage = qrCanvas.toDataURL('image/png');
        const qrImg = new Image();
        qrImg.src = qrImage;

        qrImg.onload = () => {
          let qrSize, yPos, textOffset;

          if (dataEvent?.soloMenores) {
            qrSize = 190;
            yPos = (canvas.height - qrSize) / 2 + 205;
            textOffset = 40;
          } else {
            qrSize = 200;
            yPos = (canvas.height - qrSize) / 2 + 60;
            textOffset = 45;
          }

          const xPos = (canvas.width - qrSize) / 2;

          ctx.drawImage(qrImg, xPos, yPos, qrSize, qrSize);

          ctx.font = 'bold 18px Arial';
          ctx.fillStyle = '#000';
          ctx.textAlign = 'center';
          ctx.fillText(dataQR.NombreCompleto, canvas.width / 2, yPos - textOffset);

          setImageSrc(canvas.toDataURL('image/png'));
          if (callback) callback(canvas);
        };

      };
    } else {
      // SIN BACKGROUND
      const qrImage = qrCanvas.toDataURL('image/png');
      const qrImg = new Image();
      qrImg.src = qrImage;

      qrImg.onload = () => {
        const qrSize = 220;
        let xPos, yPos;

        if (download) {
          canvas.width = qrSize + 80;
          canvas.height = qrSize + 120;

          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          ctx.font = 'bold 18px Arial';
          ctx.fillStyle = '#1769aa';
          ctx.textAlign = 'center';
          ctx.fillText(dataQR.NombreCompleto, canvas.width / 2, 30);

          ctx.font = 'bold 16px Arial';
          ctx.fillStyle = '#000';
          ctx.fillText('Folio: ' + dataQR.Folio, canvas.width / 2, 55);

          xPos = (canvas.width - qrSize) / 2;
          yPos = 70;

          ctx.drawImage(qrImg, xPos, yPos, qrSize, qrSize);

          if (callback) callback(canvas);
        } else {
          canvas.width = qrSize + 40;
          canvas.height = qrSize + 40;

          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          xPos = (canvas.width - qrSize) / 2;
          yPos = (canvas.height - qrSize) / 2;

          ctx.drawImage(qrImg, xPos, yPos, qrSize, qrSize);

          setImageSrc(canvas.toDataURL('image/png'));
          if (callback) callback(canvas);
        }
      };
    }
  };

  useEffect(() => {
    if (dataQR) generateImage(false); // vista previa inicial sin texto
    // eslint-disable-next-line
  }, [dataQR]);

  const handleDownloadImage = () => {
    generateImage(true, (canvas) => {
      canvas.toBlob((blob) => {
        saveAs(blob, `QR_${dataQR.NombreCompleto}.png`);
      }, 'image/png');
    });
  };

  const handleDownloadPDF = () => {
    generateImage(true, (canvas) => {
      const imgData = canvas.toDataURL('image/png');

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: 'a4'
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const imgHeight = pageHeight / 2;
      const aspectRatio = canvas.width / canvas.height;
      const imgWidth = imgHeight * aspectRatio;

      const x = (pageWidth - imgWidth) / 2;
      const y = (pageHeight - imgHeight) / 2;

      pdf.addImage(imgData, 'PNG', x, y, imgWidth, imgHeight);
      pdf.save(`QR_${dataQR.NombreCompleto}.pdf`);
    });
  };

  useImperativeHandle(ref, () => ({
    handleDownloadImage,
    handleDownloadPDF,
  }));

  return (
    <Box component="section" pt={view ? 0 : 6} my={view ? 0 : 6} sx={{ alignItems: 'center' }}>
      <Container component="main" maxWidth="md">
        <Card
          elevation={6}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            p: 4,
            borderRadius: 4,
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
          }}
        >
          {
            mensaje && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  mb: 2
                }}
              >
                <Typography sx={{ fontSize: { xs: 18, md: 22 }, color: '#00a152', fontWeight: 800, textAlign: 'center', textShadow: '2px 2px 6px rgba(0,0,0,0.15)' }}>
                  {mensaje}
                </Typography>
              </Box>
            )
          }

          {dataQR && (
            <div style={{ width: '100%', height: '100%', marginTop: view ? 0 : 30, textAlign: 'center' }} ref={qrRef}>
              <Grid container justifyContent="center" alignItems="center">
                <Grid item xs={12}>
                  {
                    !view && (
                      <>
                        <Typography sx={{ color: '#1769aa', fontSize: 24, fontWeight: 600 }} gutterBottom>
                          Pase Personal
                        </Typography>

                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleDownloadImage}
                          endIcon={<DownloadForOffline />}
                          sx={{ mx: 1 }}
                        >
                          Descargar PNG
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleDownloadPDF}
                          endIcon={<PictureAsPdf />}
                          sx={{ mx: 1 }}
                        >
                          Descargar PDF
                        </Button>
                      </>
                    )
                  }
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'none' }}>
                    <QRCode
                      value={dataQR.UUID}
                      fgColor="black"
                      bgColor="white"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} mt={4}>
                  <Typography sx={{ color: '#1769aa', fontSize: 28, fontWeight: 600 }} gutterBottom>
                    {dataQR.NombreCompleto}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  {
                    dataQR.Folio && (
                      <Typography variant="h6" gutterBottom>
                        Folio: <strong>{dataQR.Folio}</strong>
                      </Typography>
                    )
                  }
                </Grid>
              </Grid>

              {imageSrc && (
                <img
                  src={imageSrc}
                  alt="Imagen con QR"
                  style={{
                    marginTop: '30px',
                    width: width <= 1000 ? '100%' : '45%',
                  }}
                />
              )}
            </div>
          )}
        </Card>
      </Container>
    </Box>
  );
})

export default QRPreregistro;
